import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigation, useTranslate } from "@refinedev/core";
import { Alert, Select, Space, Typography, message as antdMessage } from "antd";
import { MessageOutlined } from "@ant-design/icons";
import { io, Socket } from "socket.io-client";
import {
  CONVERSATIONS_API_URL,
  isConversationsConfigured,
  Channel,
  ConversationListItem,
  QuickFilter,
  conversationsApi,
  getAccessToken,
} from "@/services/conversations.api";
import { ContactMatch } from "@/services/contacts.api";
import { SIN_VENTANA, codigoDeError, threadIdFor } from "@/services/channel-types";
import { Inbox } from "./Inbox";
import { Chat } from "./Chat";
import { ContactPanel } from "./ContactPanel";
import { AdvancedFilters, AdvancedFilterValue } from "./AdvancedFilters";
import { NewMessageModal } from "./NewMessageModal";
import { ConversationsEmptyChannels } from "./EmptyChannels";
import { useSearchParams } from "react-router-dom";
import { InboxUpdate, aplicarInboxUpdate } from "./inbox-patch";
import { storeDeLaBandeja } from "./store-compartido";
import { useOperatorStores } from "@/hooks/useOperatorStores";
import { useSelectedStores } from "@/config/context/SelectedItemsContext";

const { Title } = Typography;



export const ConversationsPage = () => {
  // `translate` y no `t`: dentro de los useEffect de abajo hay variables locales `t` (timeouts)
  // que lo shadowearían.
  const translate = useTranslate();
  const { push } = useNavigation();
  // Los stores donde puede OPERAR, no los que administra: incluye los linkeados de sus sellers,
  // igual que el selector de Nuevo Presupuesto. Antes salía del bucket del token, que sólo tiene
  // `store_permission` — a un operador de seller le mostraba "No tenés stores asignados".
  const { stores: storesDelOperador, loading: cargandoStores } = useOperatorStores();
  const { selectedStores, addStore, clearStores } = useSelectedStores();
  const stores = useMemo(() => storesDelOperador.map((s) => s.code), [storesDelOperador]);
  const [store, setStore] = useState<string>();
  const [inboxWidth, setInboxWidth] = useState(327);
  const [isDragging, setIsDragging] = useState(false);
  const dragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const delta = e.clientX - dragStartX.current;
      setInboxWidth(Math.min(520, Math.max(220, dragStartWidth.current + delta)));
    };
    const onMouseUp = () => {
      dragging.current = false;
      setIsDragging(false);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);
  // undefined = aún no sabemos; false = el store no tiene canales (muestra empty state).
  const [hasChannels, setHasChannels] = useState<boolean | undefined>(undefined);
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<ConversationListItem>();
  const [quickFilter, setQuickFilter] = useState<QuickFilter>("all");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [advanced, setAdvanced] = useState<AdvancedFilterValue>({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [newMsgOpen, setNewMsgOpen] = useState(false);
  // Texto preescrito que llega por deep-link (N6). Lo consume el Chat una sola vez.
  const [chatDraft, setChatDraft] = useState("");
  /**
   * Mensaje al que hay que saltar al abrir la conversación (change
   * `mensaje-con-link-al-presupuesto`, Fase 5): llega por `?open=<conv>&message=<id>` desde el
   * historial del presupuesto o del contacto. Se consume una vez; el Chat avisa cuando terminó.
   */
  const [focusMessageId, setFocusMessageId] = useState<string | null>(null);
  const [newMsgChannelId, setNewMsgChannelId] = useState<string | undefined>();
  const [newMsgContact, setNewMsgContact] = useState<ContactMatch | undefined>();
  const [newMsgStoreCode, setNewMsgStoreCode] = useState<string | undefined>();
  /**
   * Señal para que el chat se lleve el foco al compose (P4). Es un contador y no un booleano porque
   * "Nuevo mensaje" sobre una conversación que YA existe devuelve la misma, y con un flag el segundo
   * intento no volvería a enfocar.
   */
  const [composeFocus, setComposeFocus] = useState(0);
  const requestComposeFocus = () => setComposeFocus((n) => n + 1);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [writableChannels, setWritableChannels] = useState<Channel[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const socketRef = useRef<Socket | null>(null);
  const joinedChannels = useRef<string[]>([]);
  /**
   * El `load` de ESTE render, para que lo usen los handlers del socket.
   *
   * El efecto del socket corre una sola vez (deps `[]`, y tiene que ser así: reconectar en cada
   * cambio de filtro sería peor). Si registrara `() => load()` capturaría el `load` del PRIMER
   * render, donde `store` todavía es `undefined` —se resuelve en un efecto async— y ese `load`
   * corta en su primera línea (`if (!store) return []`). Resultado: recibir un mensaje no
   * actualizaba nada, mientras que enviar sí, porque el envío llama a `load()` desde un closure
   * fresco. El ref se reasigna en cada render, así que los handlers siempre ven el `load` de ahora.
   */
  const loadRef = useRef<(silent?: boolean) => Promise<ConversationListItem[]>>();
  const parcharFilaRef = useRef<(u: InboxUpdate) => void>();
  const marcarLeidaLocalRef = useRef<(conversationId: string) => void>();

  /**
   * El store visible sale del **filtro compartido** con Quotes, Contactos y Pedidos.
   *
   * Antes era un `useState` local: cambiar de pantalla desmontaba el componente y la elección se
   * perdía, así que Conversaciones era la única sección que reseteaba el filtro en cada visita.
   *
   * El filtro compartido es **multi**-select y la bandeja es de a un store, así que se toma el
   * primero de los elegidos **al que el operador tenga acceso acá**. Si ninguno lo es —o no hay
   * nada elegido— se cae al default de abajo y **no se toca la selección compartida**: pisarla
   * borraría un filtro que el operador puso en otra pantalla.
   */
  const storeCompartido = useMemo(
    () => storeDeLaBandeja(selectedStores, stores),
    [selectedStores, stores],
  );

  useEffect(() => {
    if (storeCompartido && storeCompartido !== store) setStore(storeCompartido);
  }, [storeCompartido]);

  // Default store: el primero que tenga canales (evita caer en un store vacío).
  useEffect(() => {
    if (store || storeCompartido || !stores.length) return;
    let active = true;
    conversationsApi
      .listChannels()
      .then((chs) => {
        if (!active) return;
        const withChannels = stores.find((s) => chs.some((c) => c.storeCode === s));
        setStore(withChannels ?? stores[0]);
      })
      .catch(() => setStore(stores[0]));
    return () => {
      active = false;
    };
  }, [stores]);

  // Debounce de búsqueda.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Socket único.
  useEffect(() => {
    // Sin URL del conv-service no hay a dónde conectarse: `""` haría que socket.io y axios
    // peguen contra el dominio del admin, que devuelve el HTML del index.
    if (!isConversationsConfigured()) return;

    const socket = io(`${CONVERSATIONS_API_URL}/conversations`, {
      transports: ["websocket"],
      auth: { token: getAccessToken() },
    });
    socketRef.current = socket;
    // Un mensaje NO reconstruye la bandeja: se parchea la fila afectada y se reordena con la misma
    // regla del servidor (`last_message_at DESC NULLS LAST`).
    //
    // Antes esto era un refetch de 100 filas por mensaje, y `setConversations` con una lista nueva
    // hace que la lista se re-monte: el scroll saltaba al tope. Con mensajes entrando seguido,
    // buscar algo en la bandeja era imposible.
    //
    // El refetch queda como plan B para lo que un parche no puede resolver: una conversación que no
    // está en la lista (nueva, o fuera del filtro) o un cambio de estado. Lo decide
    // `aplicarInboxUpdate` devolviendo `null`.
    socket.on("inbox:update", (p: InboxUpdate) => {
      if (!p?.conversationId) return void loadRef.current?.();
      parcharFilaRef.current?.(p);
    });
    // Alguien —yo u otro operador del store— leyó una conversación. El no-leído es compartido, así
    // que hay que reflejarlo, pero leer NO cambia el orden ni el preview: se parchea esa fila en
    // memoria. Antes esto venía como `inbox:update` y disparaba un refetch de 100 filas en cada
    // click, que era el parpadeo del listado.
    socket.on("inbox:read", (p: { conversationId: string }) => {
      if (p?.conversationId) marcarLeidaLocalRef.current?.(p.conversationId);
    });
    socket.on("channel:status", () => void loadRef.current?.());
    // #177: WhatsApp restringió (o liberó) la cuenta de un canal. Refrescar la bandeja trae el
    // `channel_restricted_until` nuevo, que es lo que bloquea el compose y pinta el cartel — sin
    // esto el operador seguiría escribiendo mensajes que WhatsApp va a rechazar.
    socket.on("channel:restriction", () => void loadRef.current?.());
    /**
     * Reconexión (reinicio del servicio, corte de red): los rooms del server se pierden → re-join
     * de los canales + resync de la bandeja para no perder lo que pasó mientras tanto.
     *
     * **El resync se saltea en la PRIMERA conexión.** `connect` también dispara al abrir la
     * sección, y ahí no hay nada que resincronizar: la bandeja se acaba de pedir por HTTP. Ese
     * refetch de 100 filas reemplazaba el array entero, React remontaba la lista y se veía un
     * parpadeo ~1 segundo después de entrar, con el scroll al tope.
     *
     * Es el mismo error que el `inbox:update` de antes: recargar todo donde no hacía falta.
     */
    let yaConectoUnaVez = false;
    socket.on("connect", () => {
      joinedChannels.current.forEach((id) => socket.emit("channel:join", { channelId: id }));
      if (yaConectoUnaVez) void loadRef.current?.();
      yaConectoUnaVez = true;
    });
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Al cambiar de store: unirse a los rooms de canal para recibir inbox:update.
  useEffect(() => {
    if (!store) return;
    let active = true;
    setHasChannels(undefined);
    conversationsApi
      .listChannels(store)
      .then(async (chs: Channel[]) => {
        if (!active) return;
        const levels = await Promise.all(
          chs.map((ch) =>
            conversationsApi.myChannelLevel(ch.channelId).catch(() => ({ level: "no_access" as const }))
          )
        );
        if (!active) return;
        const accessible = chs.filter((_, i) => levels[i].level !== "no_access");
        setHasChannels(accessible.length > 0);
        setChannels(accessible);
        // Para ARRANCAR una conversación hace falta Editor: `create()` rechaza al viewer con 403.
        // Se deriva de los niveles que ya se pidieron, sin request extra. `channels` sigue siendo el
        // set accesible completo, porque los filtros y las etiquetas del listado necesitan también
        // los de sólo lectura.
        setWritableChannels(chs.filter((_, i) => levels[i].level === "editor"));
        joinedChannels.current.forEach((id) =>
          socketRef.current?.emit("channel:leave", { channelId: id }),
        );
        joinedChannels.current = accessible.map((c) => c.channelId);
        joinedChannels.current.forEach((id) =>
          socketRef.current?.emit("channel:join", { channelId: id }),
        );
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [store]);

  // Recarga de la bandeja ante cambios de store/filtros/búsqueda.
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, quickFilter, debounced, advanced]);

  // Se reasigna en cada render: los handlers del socket leen siempre el `load` actual (ver loadRef).
  loadRef.current = load;
  parcharFilaRef.current = parcharFila;

  /**
   * Marcar leído no justifica re-pedir la bandeja.
   *
   * Antes, cada click en una conversación encadenaba `markRead` → `load(true)`: un request de 100
   * filas con joins para poner en cero el contador de UNA, más el re-render completo de la lista.
   * Eso es el parpadeo y buena parte de la latencia al cambiar de chat. Lo único que cambia es
   * `unread_count`, así que se actualiza acá y listo.
   */
  // Mismo motivo que `loadRef`: el efecto del socket corre una sola vez y capturaría la versión
  // del primer render. Ver el comentario de loadRef.
  marcarLeidaLocalRef.current = marcarLeidaLocal;

  function marcarLeidaLocal(conversationId: string): void {
    setConversations((prev) =>
      prev.map((c) => (c.conversation_id === conversationId ? { ...c, unread_count: 0 } : c)),
    );
    setSelected((prev) =>
      prev && prev.conversation_id === conversationId ? { ...prev, unread_count: 0 } : prev,
    );
  }

  /**
   * Aplica un `inbox:update` sobre la lista en memoria, y cae al refetch sólo si no alcanza.
   *
   * Va por ref por lo mismo que `load`: el efecto del socket corre una vez y capturaría la versión
   * del primer render.
   */
  function parcharFila(u: InboxUpdate): void {
    let hayQueRecargar = false;
    setConversations((prev) => {
      const patch = aplicarInboxUpdate(prev, u);
      if (!patch) {
        hayQueRecargar = true;
        return prev;
      }
      return patch;
    });
    // Fuera del updater: `setConversations` puede correr dos veces en StrictMode y no queremos dos
    // refetch. El flag se lee una sola vez, ya resuelto.
    if (hayQueRecargar) void loadRef.current?.(true);
  }

  async function load(silent = false): Promise<ConversationListItem[]> {
    if (!store) return [];
    if (!silent) setLoading(true);
    try {
      const items = await conversationsApi.listConversations({
        storeCode: store,
        // #117: "No participants" del panel de filtros = quickFilter no_operators.
        filter: advanced.noParticipants ? "no_operators" : quickFilter,
        q: debounced || undefined,
        status: advanced.status,
        channelId: advanced.channelId,
        company: advanced.company,
        participantUserId: advanced.participantUserId,
        dateFrom: advanced.dateFrom,
        dateTo: advanced.dateTo,
        contactIds: advanced.contactIds,
        limit: 100,
      });
      setConversations(items);
      // Mantener seleccionada la conversación si sigue en la lista (refresca su metadata).
      setSelected((prev) => (prev ? items.find((i) => i.conversation_id === prev.conversation_id) ?? prev : prev));
      return items;
    } catch {
      if (!silent) setConversations([]);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }

  const filtersActive = Boolean(
    advanced.status ||
      advanced.channelId ||
      advanced.company ||
      advanced.participantUserId ||
      advanced.dateFrom ||
      advanced.contactGroupId ||
      advanced.noParticipants,
  );

  // Deep-link desde la campanita del sider: /conversations?open=<conversationId>. Con `message`
  // además —desde el historial—, el chat se abre parado en ese mensaje.
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId) return;
    const messageId = searchParams.get("message");
    setSearchParams({}, { replace: true });
    // El foco se fija sólo si la conversación se abrió: si no (403/404), un `focusMessageId` colgado
    // haría que la próxima conversación abierta a mano buscara un mensaje ajeno hasta 10 páginas.
    void openFromNotification(openId).then((abierta) => setFocusMessageId(abierta ? messageId || null : null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /**
   * Deep-link desde la ficha de presupuesto o contacto.
   *
   * Con `channelId` + `phone` viene todo resuelto (N6): se abre —o se crea— la conversación de ese
   * número en ese canal y se preescribe `draft` en el compose, listo para enviar. Es el caso
   * "compartir el presupuesto": el operador no tiene que volver a buscar el contacto ni tipear el
   * link.
   *
   * Con `contactId` + `contactName` (desde ficha o tabla de contactos): se abre el modal con el
   * contacto pre-seleccionado, el operador solo elige el canal.
   *
   * Sin esos parámetros se cae al comportamiento viejo: abrir el modal de "Nuevo mensaje" vacío.
   */
  useEffect(() => {
    const newMsg = searchParams.get("newMsg");
    if (!newMsg) return;
    const channelId = searchParams.get("channelId");
    const phone = searchParams.get("phone");
    const draft = searchParams.get("draft") ?? "";
    const contactIdParam = searchParams.get("contactId");
    const contactName = searchParams.get("contactName");
    const email = searchParams.get("email") ?? undefined;
    const whatsapp = searchParams.get("whatsapp") ?? undefined;
    const contactStoreCode = searchParams.get("storeCode") ?? undefined;
    setSearchParams({}, { replace: true });

    if (channelId && phone) {
      void (async () => {
        try {
          // El tipo decide la forma del hilo (D20): un JID en WhatsApp Web, los dígitos en la Cloud
          // API. Sin encontrar el canal se manda el JID, que el backend también acepta en los dos.
          const channel = (await conversationsApi.listWritableChannels().catch(() => [])).find(
            (c) => c.channelId === channelId,
          );
          const conv = await conversationsApi.createConversation({
            channelId,
            externalThreadId: threadIdFor(channel?.type, phone),
            contactId: contactIdParam ? Number(contactIdParam) : undefined,
          });
          const convId =
            conv?.conversation_id ?? (conv as unknown as { conversationId?: string })?.conversationId;
          if (!convId) throw new Error("La conversación se creó pero no vino su id");

          setChatDraft(draft);
          await openFromNotification(convId);
          // Llega con el mensaje ya escrito: el foco va al compose para poder enviar de una (P4).
          requestComposeFocus();
          void load();
        } catch (e: any) {
          // Un canal de la Cloud API y un número que no escribió en las últimas 24 h: no es un error, y
          // el modal no tendría nada mejor que ofrecer (D1).
          if (codigoDeError(e) === "OUTSIDE_SERVICE_WINDOW") {
            antdMessage.warning(`Ese número no escribió en las últimas 24 h. ${SIN_VENTANA}`);
            return;
          }
          antdMessage.error(
            `No se pudo abrir la conversación: ${e?.response?.data?.message ?? e?.message ?? e}`,
          );
          setNewMsgOpen(true);
        }
      })();
      return;
    }

    if (contactIdParam && contactName) {
      setNewMsgContact({ contactId: Number(contactIdParam), name: contactName, email, whatsapp });
      setNewMsgStoreCode(contactStoreCode);
      if (channelId) setNewMsgChannelId(channelId);
    } else {
      setNewMsgContact(undefined);
      setNewMsgStoreCode(undefined);
    }
    setNewMsgOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /**
   * Abrir una conversación desde la campanita: puede estar fuera del filtro o en otro
   * store. Se trae el detalle (shape del listado), se alinea el store y se selecciona.
   */
  /** `true` si la conversación quedó seleccionada. */
  async function openFromNotification(conversationId: string): Promise<boolean> {
    const inList = conversations.find((c) => c.conversation_id === conversationId);
    if (inList) {
      setSelected(inList);
      return true;
    }
    try {
      const item = await conversationsApi.getConversation(conversationId);
      if (item.store_code && item.store_code !== store) setStore(item.store_code);
      setSelected(item);
      return true;
    } catch (e: any) {
      // Mencionado que perdió acceso al canal: el backend devuelve 403/404.
      antdMessage.error("No tenés acceso a esa conversación (permiso del canal revocado).");
      return false;
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 64px)",
      }}
    >
      {/* Header transparente (#110): sin fondo ni barra, hereda el bg del layout (Figma 4682-35435). */}
      <Space
        align="center"
        style={{
          padding: "12px 16px",
          display: "flex",
        }}
      >
        <MessageOutlined style={{ fontSize: 22, color: "#262626" }} />
        <Title level={4} style={{ margin: 0, color: "#262626" }}>
          {translate("conversations.title")}
        </Title>
        <Select
          mode="multiple"
          maxTagCount={1}
          style={{ width: 260, marginLeft: 8 }}
          placeholder="Store"
          value={store ? [store] : []}
          onChange={(vals: string[]) => {
            const code = vals[vals.length - 1];
            setStore(code);
            // Elegir acá también cambia el filtro de las otras pantallas: es un solo filtro de
            // store para toda la app. Como la bandeja es de a uno, la selección queda en uno solo.
            const elegido = storesDelOperador.find((s) => s.code === code);
            clearStores();
            if (elegido) {
              addStore({ code: elegido.code, name: elegido.name, seller_id: elegido.storeId });
            }
          }}
          loading={cargandoStores}
          options={storesDelOperador.map((s) => ({ value: s.code, label: s.name }))}
        />
      </Space>

      {/* Sólo cuando terminó de cargar: mientras el pedido está en vuelo la lista está vacía por
          definición, y el cartel decía que no tenés stores antes de haber preguntado. */}
      {!cargandoStores && stores.length === 0 && (
        <Alert type="warning" showIcon style={{ margin: 16 }} message="No tenés stores asignados." />
      )}

      <div style={{ flex: 1, display: "flex", minHeight: 0, background: "#fff", borderRadius: 8, overflow: "hidden", border: "1px solid #f0f0f0", userSelect: isDragging ? "none" : undefined }}>
        <Inbox
          width={inboxWidth}
          conversations={hasChannels === false ? [] : conversations}
          loading={hasChannels === false ? false : loading}
          selectedId={selected?.conversation_id}
          onSelect={setSelected}
          search={search}
          onSearch={setSearch}
          quickFilter={quickFilter}
          onQuickFilter={setQuickFilter}
          onOpenFilters={() => setFiltersOpen(true)}
          filtersActive={filtersActive}
          channels={channels}
          writableChannels={writableChannels}
          onNewMessage={(channelId: string) => {
            setNewMsgChannelId(channelId);
            setNewMsgOpen(true);
          }}
          onStatusChange={() => void load(true)}
          onOperatorClick={(contactId) => push(`/contacts/${contactId}`)}
        />
        <div
          onMouseDown={(e) => {
            dragging.current = true;
            setIsDragging(true);
            dragStartX.current = e.clientX;
            dragStartWidth.current = inboxWidth;
            e.preventDefault();
          }}
          style={{
            width: 4,
            flexShrink: 0,
            cursor: "col-resize",
            background: "transparent",
            position: "relative",
            zIndex: 1,
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = "#e0e0e0"; }}
          onMouseLeave={(e) => { if (!dragging.current) (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
        />
        {/* Store sin canales → guía a configurar canales. Si no, el chat. */}
        {store && hasChannels === false ? (
          <ConversationsEmptyChannels store={store} />
        ) : (
          <Chat
            conversation={selected}
            socket={socketRef.current}
            onOpenContact={() => setContactOpen(true)}
            onStatusChange={() => void load(true)}
            onRead={marcarLeidaLocal}
            initialDraft={chatDraft}
            onDraftConsumed={() => setChatDraft("")}
            focusMessageId={focusMessageId}
            onMessageFocused={() => setFocusMessageId(null)}
            focusComposerSignal={composeFocus}
          />
        )}
      </div>

      <AdvancedFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        storeCode={store ?? ""}
        value={advanced}
        onApply={setAdvanced}
      />
      <ContactPanel
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        conversation={selected}
        storeCode={store ?? ""}
      />
      <NewMessageModal
        open={newMsgOpen}
        onClose={() => { setNewMsgOpen(false); setNewMsgChannelId(undefined); setNewMsgContact(undefined); setNewMsgStoreCode(undefined); }}
        storeCode={newMsgStoreCode ?? store ?? ""}
        initialChannelId={newMsgChannelId}
        initialContact={newMsgContact}
        onCreated={async (conv, opts) => {
          setNewMsgOpen(false);
          setNewMsgContact(undefined);
          // `POST /conversations` devuelve el shape del listado (snake_case) en los dos caminos —ya
          // existía o se acaba de crear—. Se acepta también el camelCase de la entidad por si algún
          // entorno corre una versión anterior del conversation-service.
          const convId =
            conv?.conversation_id ?? (conv as unknown as { conversationId?: string })?.conversationId;
          if (!convId) {
            antdMessage.error("La conversación se creó pero no vino su id");
            return;
          }
          // Mismo camino que el deep-link y la campanita: NO se busca en `items`, porque la
          // conversación nueva puede quedar fuera del filtro activo (Míos, Sin operadores, un search
          // tipeado) y entonces el chat abría vacío aun con el id correcto.
          await openFromNotification(convId);
          // Sin ventana el chat abre como siempre, con el aviso de las 24 h y su «Enviar plantilla»: el
          // selector no se abre solo, que el operador primero vea a quién le está escribiendo.
          // Con ventana, vino a escribir: que no tenga que clickear el input (P4).
          if (!opts?.viaTemplate) requestComposeFocus();
          void load();
        }}
      />
    </div>
  );
};

export default ConversationsPage;
